/**
 * Sentry Error Tracking Configuration
 *
 * Loaded lazily so the SDK does not inflate the initial storefront bundle.
 */

import { logger } from './logger';

type SentryModule = typeof import('@sentry/react');

let sentryModulePromise: Promise<SentryModule | null> | null = null;
let sentryReady = false;

function loadSentry(): Promise<SentryModule | null> {
  if (!sentryModulePromise) {
    sentryModulePromise = import('@sentry/react')
      .then((mod) => mod)
      .catch((error) => {
        logger.error('Failed to load Sentry SDK:', error);
        return null;
      });
  }

  return sentryModulePromise;
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development';
  const release = import.meta.env.VITE_SENTRY_RELEASE || 'store@unknown';
  const enabled = import.meta.env.VITE_SENTRY_ENABLED === 'true' || environment === 'production';

  if (!dsn) {
    logger.debug('Sentry DSN not configured, skipping initialization');
    return;
  }

  if (!enabled) {
    logger.debug('Sentry disabled in', environment, 'environment');
    return;
  }

  void loadSentry().then((Sentry) => {
    if (!Sentry || sentryReady) return;

    try {
      Sentry.init({
        dsn,
        environment,
        release,
        tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        ignoreErrors: [
          'top.GLOBALS',
          'chrome-extension://',
          'moz-extension://',
          'NetworkError',
          'Network request failed',
          'Failed to fetch',
          'ResizeObserver loop limit exceeded',
          'ResizeObserver loop completed with undelivered notifications',
          'Session is expired',
          'refresh_token_not_found',
        ],
        enabled,
        attachStacktrace: true,
        normalizeDepth: 10,
        beforeSend(event) {
          if (!enabled) {
            return null;
          }

          if (
            event.exception?.values?.[0]?.value?.includes('401') ||
            event.exception?.values?.[0]?.value?.includes('403')
          ) {
            logger.debug('Filtered out auth error from Sentry:', event.exception.values[0].value);
            return null;
          }

          event.tags = {
            ...event.tags,
            browser: navigator.userAgent,
          };

          return event;
        },
        beforeBreadcrumb(breadcrumb) {
          if (environment === 'production' && breadcrumb.category === 'console') {
            return null;
          }

          if (breadcrumb.data?.url) {
            breadcrumb.data.url = redactSensitiveData(String(breadcrumb.data.url));
          }

          return breadcrumb;
        },
      });

      sentryReady = true;
      logger.debug('Sentry initialized successfully', { environment, release });
    } catch (error) {
      logger.error('Failed to initialize Sentry:', error);
    }
  });
}

export function setSentryUser(user: { id: string; email?: string; username?: string } | null): void {
  void loadSentry().then((Sentry) => {
    if (!Sentry) return;

    if (!user) {
      Sentry.setUser(null);
      return;
    }

    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username || user.email,
    });

    logger.debug('Sentry user context set:', user.email);
  });
}

export function captureException(error: Error | string, context?: Record<string, any>): void {
  void loadSentry().then((Sentry) => {
    if (!Sentry) return;

    if (context) {
      Sentry.withScope((scope) => {
        Object.entries(context).forEach(([key, value]) => {
          scope.setContext(key, value);
        });
        Sentry.captureException(error);
      });
    } else {
      Sentry.captureException(error);
    }
  });

  logger.error('Exception captured by Sentry:', error, context);
}

export function captureMessage(
  message: string,
  level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info'
): void {
  void loadSentry().then((Sentry) => {
    if (!Sentry) return;
    Sentry.captureMessage(message, level);
  });

  logger.debug('Message captured by Sentry:', message, level);
}

export function addBreadcrumb(breadcrumb: {
  message: string;
  category?: string;
  level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
  data?: Record<string, any>;
}): void {
  void loadSentry().then((Sentry) => {
    if (!Sentry) return;
    Sentry.addBreadcrumb(breadcrumb);
  });
}

function redactSensitiveData(input: string): string {
  return input
    .replace(/([?&](token|access_token|refresh_token|password|secret)=)[^&]+/gi, '$1[REDACTED]')
    .replace(/(Bearer\s+)[A-Za-z0-9._-]+/gi, '$1[REDACTED]');
}

